/**
 * FENX Custom -- GA4 Client ID & Session ID Capture (Growth-safe, cart-attribute)
 * ==============================================================================
 *
 * Captures the GA4 client_id and session_id from first-party cookies on the
 * STOREFRONT and writes them as Shopify CART ATTRIBUTES. The order then carries
 * them as note attributes, which the order handler reads directly -- the SAME
 * cookie -> cart-attribute -> note-attribute path used for the click ids
 * (Layer 1:launch-critical, see click-id-capture.js). There is no promotion step and no
 * order metafield. The server reads those note attributes to stitch server events
 * (purchase_verified, scan_token_sent, ...) to the browser session.
 *
 * WHY cart attribute, NOT thank-you / checkout capture:
 *   On the Shopify Growth plan the theme does not run on the checkout, and
 *   order-status / thank-you-page scripts are unreliable (DD-002; implementation
 *   -guide Step 3.2:identifier-sourcing). Capture must therefore happen on the storefront/cart,
 *   BEFORE checkout, by writing a cart attribute -- not by posting from the
 *   order-status page. This file deliberately does not depend on Shopify.checkout
 *   or the checkout_completed pixel event.
 *
 * Consent:
 *   client_id/session_id capture is best-effort for the pre-login anonymous
 *   window (DD-006). No explicit gate is needed for WRITING: under Consent Mode v2
 *   the `_ga` / `_ga_*` cookies only exist when analytics_storage was granted, so a
 *   denied visitor has nothing to read and nothing is written.
 *
 *   That argument does NOT cover WITHDRAWAL, and an earlier version of this file
 *   stopped there. Ids written under an earlier grant stay in the cart after the
 *   visitor revokes: Consent Mode clears the cookies, but the cart attribute
 *   survives and rides onto the order. So this file also RETRACTS -- clearing both
 *   cart attributes on an analytics denial, mirroring what the click-id path
 *   already does. Raised by both 2026-08-07 source-code reviews.
 *
 *   Consent is read from whichever mechanism is live: `window.fenxCookieConsent`
 *   when it exists (DD-008, not built as of 2026-08-07), else Shopify's Customer
 *   Privacy API, which is what is actually running. Tri-state -- 'yes' | 'no' | ''
 *   -- and '' (undecided) must never be treated as a denial, or a visitor who has
 *   not answered the banner gets their ids cleared. `visitorConsentCollected` fires
 *   only on a CHANGE, never on listener attach, so current state is read directly
 *   as well as subscribed to.
 *
 * Platform:
 *   Storefront theme snippet on fenxcustom.com (main browsing context, full
 *   first-party cookie access). NOT the Web Pixels sandbox, which cannot read the
 *   _ga cookies or set the main cart's attributes.
 *   <script src="{{ 'ga4-client-id-capture.js' | asset_url }}" defer></script>
 *
 * Debug mode:
 *   Append ?debug_ga4_capture=true to any page URL for verbose logging.
 *
 * @version 2.0.0
 * @author  FENX Custom
 */

(function () {
  "use strict";

  // =========================================================================
  // Configuration
  // =========================================================================

  // The unsubstituted value. Named rather than inlined so the refusal below and the config
  // entry cannot drift apart.
  var PLACEHOLDER_MEASUREMENT_ID = "G-XXXXXXXXXX";

  var CONFIG = {
    /**
     * Substituted at install by the THEME, from a theme setting:
     *
     *   window.fenxGa4MeasurementId = '{{ settings.ga4_measurement_id }}';
     *
     * Same mechanism as the fenxGa4CaptureEnabled flag below, so there is one way to
     * configure this script rather than two, staging and production differ by a setting
     * and not by a file, and the asset deployed is byte-identical to the file reviewed.
     * A blank or missing setting leaves the placeholder, and init refuses to run on it.
     */
    GA4_MEASUREMENT_ID:
      (typeof window !== "undefined" && window.fenxGa4MeasurementId) || PLACEHOLDER_MEASUREMENT_ID,

    // Cart attribute keys, which Shopify carries onto the order as note attributes and the
    // order handler reads directly (shared Layer 1:launch-critical path; see
    // implementation-guide Step 3.2:identifier-sourcing). The keys are fenx-prefixed, like
    // `fenx_click_ids`, to avoid collisions with anything else on the cart.
    CART_CLIENT_ID_KEY: "fenx_ga4_client_id",
    CART_SESSION_ID_KEY: "fenx_ga4_session_id",

    /** sessionStorage key: last client|session pair already written this session. */
    SYNCED_KEY: "fenx_ga4_ids_synced",

    /** sessionStorage key: cart ids already cleared once after a consent withdrawal. */
    // A sessionStorage key name, not a credential. gitleaks' generic-api-key rule matches
    // on `..._KEY: '<high-entropy string>'` and cannot tell the two apart.
    DENIED_CLEARED_KEY: "fenx_ga4_ids_denied_cleared", // gitleaks:allow

    /**
     * Cookie the tester sets deliberately. Without it the script does not run, so an
     * ordinary visitor never reaches the capture path. This half is proven on PRODUCTION:
     * staging serves no Google tag (DD-005:staging-separation), so `_ga` cookies never
     * exist there and the script cannot be exercised at all.
     */
    TESTER_COOKIE: "fenx_ga4_capture_tester",

    /**
     * Global the theme writes from its own setting, read on EVERY load. Setting it false
     * stops capture for every visitor with no code deploy. The server-side send kill
     * switch does NOT cover this -- it stops the handler, not a browser script writing
     * cart attributes.
     */
    ENABLED_FLAG: "fenxGa4CaptureEnabled",

    /** Console log prefix */
    LOG_PREFIX: "[FenX GA4 capture]",

    /** Query param that turns on debug logging */
    DEBUG_PARAM: "debug_ga4_capture",

    /**
     * Query param that arms or clears TESTER_COOKIE -- `on` or `off`. Testing runs on
     * production (DD-011:stage-degrades-production-gated), so the switch has to be
     * throwable from the address bar on any device, not from developer tools.
     */
    TESTER_PARAM: "fenx_ga4_tester",
  };

  var DEBUG =
    typeof window !== "undefined" &&
    window.location &&
    new URLSearchParams(window.location.search).get(CONFIG.DEBUG_PARAM) === "true";

  function debugLog() {
    if (!DEBUG) return;
    var args = [CONFIG.LOG_PREFIX + " [DEBUG]"];
    for (var i = 0; i < arguments.length; i++) {
      args.push(arguments[i]);
    }
    try {
      console.log.apply(console, args);
    } catch {
      // Swallow -- never break the page
    }
  }

  // =========================================================================
  // Cookie Parsing Utilities (unchanged -- these read the standard GA4 cookies)
  // =========================================================================

  /**
   * Parse document.cookie to retrieve the value of a named cookie.
   *
   * @param {string} name - Cookie name to look up
   * @returns {string|null} Cookie value, or null if not found
   */
  function getCookie(name) {
    try {
      if (typeof document === "undefined" || !document.cookie) {
        return null;
      }

      var cookies = document.cookie.split(";");
      var target = name + "=";

      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i];
        // Trim leading whitespace
        while (cookie.charAt(0) === " ") {
          cookie = cookie.substring(1);
        }
        if (cookie.indexOf(target) === 0) {
          return decodeURIComponent(cookie.substring(target.length));
        }
      }

      return null;
    } catch (err) {
      debugLog('Error reading cookie "' + name + '":', err);
      return null;
    }
  }

  /**
   * Arm or clear the tester cookie from a URL parameter.
   *
   * Testing happens on PRODUCTION, because staging is a password-protected development
   * store that no application reaches, so `_ga` never exists there and this script cannot
   * be exercised at all (DD-011:stage-degrades-production-gated). A production test
   * therefore needs a switch a tester can throw from the address bar on any device --
   * `?fenx_ga4_tester=on` to arm, `?fenx_ga4_tester=off` to clear. Without this the cookie
   * could only be set by hand in developer tools, which is not available to a tester on a
   * phone.
   *
   * Session cookie, deliberately: it lasts the browser session and then clears itself,
   * so there is no expiry period to choose and no tester left permanently armed.
   *
   * Runs BEFORE the tester-cookie gate below, so arming takes effect on the same page load
   * that sets it. Any value other than `on` or `off` is ignored rather than guessed at.
   */
  function applyTesterParam() {
    try {
      if (typeof window === "undefined" || !window.location || typeof document === "undefined") {
        return;
      }
      var value = new URLSearchParams(window.location.search).get(CONFIG.TESTER_PARAM);
      if (value !== "on" && value !== "off") return;

      if (value === "on") {
        document.cookie = CONFIG.TESTER_COOKIE + "=1; Path=/; SameSite=Lax; Secure";
        console.warn(CONFIG.LOG_PREFIX, "tester cookie ARMED for this browser session.");
      } else {
        document.cookie =
          CONFIG.TESTER_COOKIE +
          "=; Path=/; SameSite=Lax; Secure; Expires=Thu, 01 Jan 1970 00:00:00 GMT";
        console.warn(CONFIG.LOG_PREFIX, "tester cookie CLEARED.");
      }
    } catch (err) {
      debugLog("Error applying tester param:", err);
    }
  }

  /**
   * Extract the GA4 client_id from the _ga cookie.
   *
   * The _ga cookie has the format:
   *   GA1.1.XXXXXXXXX.XXXXXXXXXX   (single-domain)
   *   GA1.2.XXXXXXXXX.XXXXXXXXXX   (cross-domain)
   *
   * The client_id is the last two dot-separated numeric segments,
   * yielding a string like "XXXXXXXXX.XXXXXXXXXX".
   *
   * @returns {string|null} GA4 client_id, or null if unavailable
   */
  function getGA4ClientId() {
    try {
      var gaCookie = getCookie("_ga");
      debugLog("Raw _ga cookie:", gaCookie);

      if (!gaCookie) {
        return null;
      }

      // Split on dots: ["GA1", "1", "XXXXXXXXX", "XXXXXXXXXX"]
      var parts = gaCookie.split(".");

      if (parts.length < 4) {
        debugLog("_ga cookie has unexpected format (fewer than 4 parts):", gaCookie);
        return null;
      }

      // client_id = last two segments joined by a dot
      var clientId = parts.slice(-2).join(".");

      // Validate: should be two numeric-ish segments separated by a dot
      if (!/^\d+\.\d+$/.test(clientId)) {
        debugLog("Extracted client_id does not match expected pattern:", clientId);
        return null;
      }

      debugLog("Parsed GA4 client_id:", clientId);
      return clientId;
    } catch (err) {
      debugLog("Error extracting GA4 client_id:", err);
      return null;
    }
  }

  /**
   * Derive the GA4 session cookie name from the Measurement ID.
   *
   * The Measurement ID has the format G-XXXXXXXXXX.
   * The corresponding session cookie is named _ga_XXXXXXXXXX
   * (the "G-" prefix is removed and replaced with "_ga_").
   *
   * @returns {string} Cookie name, e.g. "_ga_XXXXXXXXXX"
   */
  function getSessionCookieName() {
    var measurementId = CONFIG.GA4_MEASUREMENT_ID || "";
    // Remove the "G-" prefix
    var streamId = measurementId.replace(/^G-/i, "");
    return "_ga_" + streamId;
  }

  /**
   * Extract the GA4 session_id from the _ga_XXXXXXX cookie.
   *
   * The cookie value has the format:
   *   GS1.1.XXXXXXXXXX.X.X.XXXXXXXXXX.X.X.X
   *
   * The session_id is the third dot-separated segment (0-indexed position 2).
   * If the specific cookie (based on Measurement ID) is not found, falls back to
   * scanning all cookies matching the _ga_ prefix pattern.
   *
   * @returns {string|null} GA4 session_id, or null if unavailable
   */
  function getGA4SessionId() {
    try {
      // First, try the specific cookie name derived from the Measurement ID
      var specificCookieName = getSessionCookieName();
      var sessionCookie = getCookie(specificCookieName);

      debugLog("Looking for session cookie:", specificCookieName, "-> value:", sessionCookie);

      // If not found by specific name, scan for any _ga_ prefixed cookie
      if (!sessionCookie) {
        debugLog("Specific session cookie not found, scanning all _ga_ cookies...");
        sessionCookie = findGA4SessionCookieByPattern();
      }

      if (!sessionCookie) {
        debugLog("No GA4 session cookie found");
        return null;
      }

      // Parse the session_id from the cookie value: GS1.1.XXXXXXXXXX.X.X.XXXXXXXXXX.X.X.X
      var parts = sessionCookie.split(".");

      if (parts.length < 3) {
        debugLog("Session cookie has unexpected format (fewer than 3 parts):", sessionCookie);
        return null;
      }

      // session_id is at index 2 (the first large number after GS1.1.)
      var sessionId = parts[2];

      // Validate: should be a numeric timestamp-like value
      if (!/^\d+$/.test(sessionId)) {
        debugLog("Extracted session_id does not appear numeric:", sessionId);
        return null;
      }

      debugLog("Parsed GA4 session_id:", sessionId);
      return sessionId;
    } catch (err) {
      debugLog("Error extracting GA4 session_id:", err);
      return null;
    }
  }

  /**
   * Scan all cookies to find one matching the _ga_ prefix pattern.
   * Fallback for when the specific Measurement ID-based cookie name does not match.
   *
   * @returns {string|null} Cookie value of the first _ga_ cookie found, or null
   */
  function findGA4SessionCookieByPattern() {
    try {
      if (typeof document === "undefined" || !document.cookie) {
        return null;
      }

      var cookies = document.cookie.split(";");

      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i];
        // Trim leading whitespace
        while (cookie.charAt(0) === " ") {
          cookie = cookie.substring(1);
        }

        // Match cookies starting with _ga_ but NOT the plain _ga= cookie
        if (cookie.indexOf("_ga_") === 0) {
          var eqIndex = cookie.indexOf("=");
          if (eqIndex > 0) {
            var cookieName = cookie.substring(0, eqIndex);
            var cookieValue = decodeURIComponent(cookie.substring(eqIndex + 1));
            debugLog("Found _ga_ cookie by pattern scan:", cookieName, "=", cookieValue);
            return cookieValue;
          }
        }
      }

      return null;
    } catch (err) {
      debugLog("Error scanning for _ga_ cookies:", err);
      return null;
    }
  }

  // =========================================================================
  // Session dedup (avoid re-POSTing the same ids on every storefront page)
  // =========================================================================

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* private mode / disabled: capture degrades to same-page only */
    }
  }

  // =========================================================================
  // Delivery: write the ids as Shopify cart attributes
  // =========================================================================

  /**
   * Write client_id/session_id as cart attributes via the Shopify AJAX Cart API.
   * POST /cart/update.js with an `attributes` object MERGES the given keys (other
   * cart attributes are untouched). Throws on a non-OK response so the caller does
   * NOT mark the ids synced -- a later storefront page then retries.
   *
   * @param {string} clientId
   * @param {string|null} sessionId
   * @returns {Promise<Response>}
   */
  async function writeCartAttributes(clientId, sessionId) {
    var attributes = {};
    attributes[CONFIG.CART_CLIENT_ID_KEY] = clientId;
    if (sessionId) {
      attributes[CONFIG.CART_SESSION_ID_KEY] = sessionId;
    }

    var res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    });
    if (!res || !res.ok) {
      throw new Error("cart/update.js failed: " + (res ? res.status : "no response"));
    }
    return res;
  }

  /**
   * Clear both id attributes from the cart. Setting a key to "" removes just that
   * key; other cart attributes (the click id, for one) are untouched.
   *
   * @returns {Promise<Response>}
   */
  async function retractCartAttributes() {
    var attributes = {};
    attributes[CONFIG.CART_CLIENT_ID_KEY] = "";
    attributes[CONFIG.CART_SESSION_ID_KEY] = "";

    var res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    });
    if (!res || !res.ok) {
      throw new Error("cart/update.js clear failed: " + (res ? res.status : "no response"));
    }
    return res;
  }

  /**
   * Read analytics consent from whichever mechanism is live, as a tri-state.
   * Returns "granted" | "denied" | "unknown". Never collapse unknown into denied:
   * that would clear ids before the visitor has answered the banner.
   *
   * @returns {string}
   */
  function readAnalyticsConsent() {
    var fenx = window.fenxCookieConsent && window.fenxCookieConsent.state;
    if (fenx) {
      if (fenx.analytics_storage === "granted") return "granted";
      // Tri-state here too. Consent Mode defaults analytics_storage to denied BEFORE the
      // visitor answers, so "not granted" is not "refused" -- reading it as a refusal
      // would clear this visitor's identifiers before the banner was ever seen.
      return fenx.answered ? "denied" : "unknown";
    }
    var sp = window.Shopify && window.Shopify.customerPrivacy;
    if (sp && typeof sp.currentVisitorConsent === "function") {
      var analytics = sp.currentVisitorConsent().analytics;
      if (analytics === "yes") return "granted";
      if (analytics === "no") return "denied";
    }
    return "unknown";
  }

  /**
   * Withdrawal handling. The file header argues no consent gate is needed for
   * WRITING, and that is right: `_ga` cookies only exist under a grant, so a denied
   * visitor has nothing to read. It does not hold for WITHDRAWAL. Ids written under
   * an earlier grant sit in the cart, survive the revoke, and ride onto the order.
   * The click-id path already retracts; this mirrors it. Raised by both
   * 2026-08-07 source-code reviews.
   *
   * Clears once per session, and only marks it cleared after the write is confirmed
   * so a failed clear retries on the next page.
   *
   * @returns {Promise<void>}
   */
  async function onAnalyticsWithdrawn() {
    sessionSet(CONFIG.SYNCED_KEY, ""); // allow a re-write if consent is granted again
    if (sessionGet(CONFIG.DENIED_CLEARED_KEY) === "1") return;
    try {
      await retractCartAttributes();
      sessionSet(CONFIG.DENIED_CLEARED_KEY, "1");
      debugLog("Analytics consent withdrawn -- cart ids cleared.");
    } catch (err) {
      console.warn(CONFIG.LOG_PREFIX, "cart attribute clear failed, will retry next page:", err);
    }
  }

  /**
   * Route a consent state to its one action. Undecided does nothing.
   *
   * @returns {Promise<void>}
   */
  async function onConsentChanged() {
    var state = readAnalyticsConsent();
    if (state === "granted") {
      sessionSet(CONFIG.DENIED_CLEARED_KEY, ""); // a later withdrawal must clear again
      await captureToCart();
    } else if (state === "denied") {
      await onAnalyticsWithdrawn();
    }
  }

  /**
   * Read the GA4 ids from cookies and, if a client_id is present, write them to
   * the cart. Deduped per (client_id, session_id) pair within a session, so it is
   * safe to call on every storefront page load; it re-writes only when the GA4
   * session rolls over (new session_id), keeping the latest session on the order.
   *
   * @returns {Promise<void>}
   */
  async function captureToCart() {
    // Gate the WRITE on a positive grant, not on the cookies happening to exist.
    // Under Consent Mode the `_ga` cookies usually vanish on a denial, so for a while this
    // looked redundant -- but "usually" is not a gate. A lingering cookie, a surface running
    // Advanced mode, or a race between the clear and the poll below would otherwise re-fill
    // the cart seconds after a withdrawal emptied it, and the order would carry ids the
    // visitor revoked. Undecided is not a grant either: promote nothing until asked.
    if (readAnalyticsConsent() !== "granted") {
      debugLog("Analytics consent is not granted -- not writing.");
      return;
    }

    var clientId = getGA4ClientId();
    var sessionId = getGA4SessionId();

    if (!clientId) {
      debugLog(
        "No GA4 client_id in cookies (ad blocker, consent denied, or GA4 not loaded yet) -- nothing to write."
      );
      return;
    }

    var pair = clientId + "|" + (sessionId || "");
    if (sessionGet(CONFIG.SYNCED_KEY) === pair) {
      debugLog("Cart already has these ids this session -- skipping.");
      return;
    }

    try {
      await writeCartAttributes(clientId, sessionId);
      sessionSet(CONFIG.SYNCED_KEY, pair); // mark synced ONLY after the write is confirmed
      debugLog("Wrote GA4 ids to cart:", pair);
    } catch (err) {
      console.warn(CONFIG.LOG_PREFIX, "cart attribute write failed, will retry next page:", err);
    }
  }

  // =========================================================================
  // Init: run on storefront pages
  // =========================================================================

  (function init() {
    if (typeof window === "undefined") return;

    // Two independent stops, both read on every load. The theme setting is the
    // everyone-scoped one; the tester cookie is the per-visitor one.
    //
    // Fail closed on the flag, and say so. An undefined flag means the theme never wired
    // the setting, which is a deploy that looks successful and captures nothing -- the one
    // outcome this script is not allowed to produce silently.
    if (window[CONFIG.ENABLED_FLAG] !== true) {
      console.warn(
        CONFIG.LOG_PREFIX,
        "disabled: window." + CONFIG.ENABLED_FLAG + " is not true (value:",
        window[CONFIG.ENABLED_FLAG],
        "). Nothing captured. The theme setting is off, or was never wired."
      );
      return;
    }

    // Arm or clear from the URL first, so ?fenx_ga4_tester=on takes effect on the same load.
    // The cookie no longer gates capture; it survives as a way to force a known state while
    // testing, and applyTesterParam still honours `on` and `off`.
    applyTesterParam();

    // **The tester gate was removed 2026-08-13.** It read:
    //     if (!getCookie(CONFIG.TESTER_COOKIE)) return;
    // and it did its job: while this script was unproven, only a browser carrying
    // `fenx_ga4_capture_tester` wrote identifiers, so a defect could not reach a real order.
    //
    // Once the script shipped, that same line became the defect. An ordinary shopper never
    // has the cookie, so no real order carried `fenx_ga4_client_id`, so the `orders/paid`
    // handler skipped every one of them -- five in a row on staging before anyone traced it
    // to this line rather than to a missing feature. A gate that cannot be satisfied by the
    // population it is meant to measure is not a gate, it is an off switch.
    //
    // What replaces it is what was always the real control: consent. Under Consent Mode v2
    // the `_ga` cookies exist only when `analytics_storage` was granted, so a denied visitor
    // has nothing to read and nothing is written. And `settings.ga4_capture_enabled` in the
    // theme is the merchant-facing off switch, which needs no code change to throw.
    //
    // Reversing DD-011's tester-gated reading for this file. `click-id-capture.js` never had
    // this gate, so until now an order could carry a `gclid` and no `client_id` -- the two
    // halves of one identity, captured on different rules.

    // The placeholder Measurement ID must never reach an environment. getSessionCookieName()
    // derives `_ga_<streamId>` from it, so a placeholder makes the script look for a cookie
    // that never exists: session_id returns null on every page while client_id still
    // populates, and a verification that checks only client_id reads as a pass.
    if (CONFIG.GA4_MEASUREMENT_ID === PLACEHOLDER_MEASUREMENT_ID) {
      console.warn(
        CONFIG.LOG_PREFIX,
        "refusing to run: GA4_MEASUREMENT_ID is still the placeholder",
        PLACEHOLDER_MEASUREMENT_ID + ".",
        "Substitute the environment's real Measurement ID at install."
      );
      return;
    }

    if (DEBUG) {
      debugLog("Config:", CONFIG.GA4_MEASUREMENT_ID, "| session cookie:", getSessionCookieName());
      debugLog("client_id:", getGA4ClientId(), "| session_id:", getGA4SessionId());
    }

    if (document.readyState === "complete" || document.readyState === "interactive") {
      captureToCart();
    } else {
      window.addEventListener("DOMContentLoaded", captureToCart);
    }

    // Late consent: `_ga` / `_ga_*` only exist AFTER analytics consent is granted, and consent can
    // be granted mid-page -- so capturing only on load would miss it (the order would then fall back
    // to a generated client_id and never stitch). Also re-run when the in-house banner resolves
    // consent, and poll briefly for the cookie appearing. captureToCart is deduped (SYNCED_KEY), so
    // the extra calls are cheap and safe.
    if (window.fenxCookieConsent && typeof window.fenxCookieConsent.onResolved === "function") {
      window.fenxCookieConsent.onResolved(onConsentChanged);
    }

    // Shopify's Customer Privacy API is what is live today. It is absent by default,
    // so ask for it; the change event carries later grants AND withdrawals.
    document.addEventListener("visitorConsentCollected", onConsentChanged);
    if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
      try {
        window.Shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          function (err) {
            if (!err) onConsentChanged();
          }
        );
      } catch {
        // Never break the page; the poll below still covers the grant case.
      }
    }
    var polls = 0;
    var pollTimer = setInterval(function () {
      // Stop chasing a grant that will not come. Without this the timer keeps calling
      // captureToCart after a denial; the write is gated now, so nothing lands, but a
      // stopped timer is the honest expression of "the visitor said no".
      if (readAnalyticsConsent() === "denied") {
        clearInterval(pollTimer);
        return;
      }
      captureToCart();
      if (getGA4ClientId() || (polls += 1) >= 10) {
        // Record an exhausted poll rather than exiting silently. An unrecorded timeout is
        // indistinguishable from a script that never ran, which is the one thing the
        // requirement binding every capture script forbids. click-id-capture.js does the same.
        // console.warn, NOT debugLog: debugLog early-returns unless the URL carries
        // ?debug_ga4_capture=true, so a debug-only record is still silence on every
        // ordinary load. click-id-capture.js warns unconditionally and this must match it.
        if (!getGA4ClientId()) {
          console.warn(
            CONFIG.LOG_PREFIX,
            "no GA4 client id resolved after ~10s of polling; nothing written.",
            "Consent state at give-up:",
            readAnalyticsConsent()
          );
        }
        clearInterval(pollTimer); // stop once the client_id is captured or after ~10 tries
      }
    }, 1000);
  })();
})();
